import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, Input } from '@saas-kit/ui';

import type { Role } from './api';

interface Props {
  roles: Role[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onCreateClick: () => void;
  isLoading?: boolean;
  canManage: boolean;
  creating: boolean;
}

export function RolesList({
  roles,
  selectedId,
  onSelect,
  onCreateClick,
  isLoading = false,
  canManage,
  creating,
}: Props): JSX.Element {
  const { t } = useTranslation('rbac');
  const [query, setQuery] = useState('');

  const sorted = useMemo(
    () =>
      [...roles].sort((a, b) => {
        if (a.is_system !== b.is_system) return a.is_system ? -1 : 1;
        return a.name.localeCompare(b.name);
      }),
    [roles],
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return sorted;
    return sorted.filter(
      (role) =>
        role.name.toLowerCase().includes(needle) ||
        role.slug.toLowerCase().includes(needle) ||
        (role.description ?? '').toLowerCase().includes(needle),
    );
  }, [query, sorted]);

  return (
    <aside className="flex h-full min-h-0 flex-col gap-3 rounded-lg border border-border bg-card p-3">
      <div className="flex items-center gap-2">
        <Input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('list.search_placeholder')}
          className="h-8 flex-1"
        />
        {canManage ? (
          <Button type="button" size="sm" onClick={onCreateClick}>
            {t('list.create_button')}
          </Button>
        ) : null}
      </div>
      <ul className="flex-1 min-h-0 space-y-1 overflow-y-auto">
        {isLoading ? (
          <li className="px-2 py-1 text-xs text-muted-foreground">{t('list.loading')}</li>
        ) : filtered.length === 0 ? (
          <li className="px-2 py-1 text-xs text-muted-foreground">
            {query ? t('list.no_match') : t('list.empty')}
          </li>
        ) : null}
        {filtered.map((role) => {
          const active = !creating && role.id === selectedId;
          return (
            <li key={role.id}>
              <button
                type="button"
                onClick={() => onSelect(role.id)}
                className={
                  'flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-start text-sm transition-colors ' +
                  (active
                    ? 'bg-primary/10 font-medium text-foreground'
                    : 'text-foreground hover:bg-muted')
                }
              >
                <div className="min-w-0">
                  <div className="truncate">{role.name}</div>
                  <div className="truncate font-mono text-[11px] text-muted-foreground">
                    {role.slug}
                  </div>
                </div>
                <Badge variant={role.is_system ? 'secondary' : 'outline'}>
                  {t(role.is_system ? 'list.kind_system' : 'list.kind_custom')}
                </Badge>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
